import { NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ElementRef, signal, Inject } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { combineLatest } from 'rxjs';
import { mergeMap, startWith, withLatestFrom } from 'rxjs/operators';
import { ConfigService } from '@mona/config';
import { DataAccessExternalResourcesFacade } from '@mona/pdms/data-access-external-resources';
import { Platform, PLATFORM, takeUntilDestroy, TakeUntilDestroy, uiPure } from '@mona/shared/utils';
import { UiModule } from '@mona/ui';
import { ExternalLink } from '../../../../../mona/api/src/lib/model';

@Component({
    template: `
        <ui-drawer-portals>
            <ng-template uiToolbarTitle>
                {{ title }}
            </ng-template>
        </ui-drawer-portals>

        <ui-drawer-content>
            <mat-tab-group ui-drawer-content-nav (selectedTabChange)="onTabChange($event)">
                <mat-tab *ngFor="let link of links">
                    <ng-template mat-tab-label>
                        <span *ngIf="link.icon">
                            <img
                                class="link-icon"
                                [width]="24"
                                [height]="24"
                                alt="icon"
                                [src]="configService.get('api.baseUrl') + link.icon"
                            />
                            &nbsp;
                        </span>
                        <span>{{ link?.name }}</span>
                    </ng-template>
                    <div class="iframe-container" [uiLoadingOverlay]="isLoading()">
                        <webview
                            *ngIf="isElectron"
                            [attr.src]="getSafeWebviewUrl(link.url)"
                            partition="persist:webview"
                            style="width: 100%; height: 100%"
                            (did-finish-load)="onIframeLoad($event)"
                            (did-fail-load)="onIframeError($event)"
                        ></webview>
                        <iframe
                            *ngIf="!isElectron"
                            #iframe
                            [src]="link.url | safeUrl: 'resource'"
                            style="width: 100%; height: 100%"
                            (load)="onIframeLoad($event)"
                            (error)="onIframeError($event)"
                        ></iframe>
                    </div>
                </mat-tab>
            </mat-tab-group>
        </ui-drawer-content>
    `,
    imports: [UiModule, NgIf, MatProgressSpinnerModule],
    standalone: true,
})
@TakeUntilDestroy
export class InternalAppComponent implements OnInit, OnDestroy {
    title: string;
    links: ExternalLink[] = [];
    isLoading = signal(true);
    isElectron = this.platform.isElectron;
    @ViewChild('iframe') iframe: ElementRef<HTMLIFrameElement>;

    constructor(
        private ar: ActivatedRoute,
        private externalResourcesFacade: DataAccessExternalResourcesFacade,
        public configService: ConfigService,
        private cdr: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
        @Inject(PLATFORM) private platform: Platform,
    ) {}

    ngOnInit() {
        combineLatest([
            this.ar.url.pipe(startWith(this.ar.snapshot.url)),
            this.externalResourcesFacade.internalApplications$,
        ])
            .pipe(takeUntilDestroy(this))
            .subscribe(([url, internalApplications]) => {
                this.links = [];
                this.title = '';

                const linksGroup = internalApplications.find(app => app.name === url[1].path);

                if (linksGroup) {
                    this.links = linksGroup.resources;
                    this.title = linksGroup.name;
                    this.cdr.detectChanges();
                }
            });
    }

    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, jsdoc/require-jsdoc
    ngOnDestroy(): void {
        // empty
    }

    onIframeLoad(event: Event): void {
        this.isLoading.set(false);
    }

    onIframeError(event: Event): void {
        console.log('iframe error: ', event);
        this.isLoading.set(false);
    }

    onTabChange(_) {
        this.isLoading.set(true);
    }

    /**
     * Sanitize and validate URL for webview
     * Webview doesn't accept SafeResourceUrl objects, so we validate and return raw string
     * @param url
     */
    @uiPure
    getSafeWebviewUrl(url: string): string {
        try {
            // Only allow http, https, and file protocols
            const allowedProtocols = ['http:', 'https:', 'file:'];
            if (!allowedProtocols.includes(new URL(url).protocol)) {
                return 'about:blank';
            }

            // Sanitize with Angular's sanitizer first to check for XSS
            const sanitized = this.sanitizer.sanitize(1, url);
            if (sanitized === null) {
                return 'about:blank';
            }

            return url;
        } catch (error) {
            return 'about:blank';
        }
    }
}
